import PropTypes from "prop-types";
import { isFuture, parseISO } from "date-fns";
import LayoutResidency from "components/layouts/LayoutResidency";
import { formatURL } from "helpers/cloudfrontURLFormatting";
import { getEvents, getEvent } from "lib/api";
import Section from "components/residency/Section";
import Introduction from "components/residency/Introduction";
import Meta from "solar/components/Meta";
import Background from "components/residency/Background";
import BannerPhone from "components/shared/adventures/BannerPhone";
import Present from "components/shared/adventures/Present";
import Navigation from "components/event/Navigation";
import Hero from "components/surfer_development/Hero";
import Container from "components/adventure/Container";
import Guide from "components/adventure/Guide";
import Testimonial from "components/adventure/Testimonial";
import Highlights from "components/adventure/Highlights";
import Questions from "components/residency/Questions";
import Why from "components/adventure/Why";
import Footer from "components/shared/adventures/Footer";
import Accommodation from "components/residency/Accommodation";

export default function Event({ event }) {
  const themeColor = event.theme_color || "brown";

  const overview = [
    {
      id: 1,
      title: "Skill level",
      description: event.skill_level,
      link: "when",
    },
    {
      id: 2,
      title: "When",
      description: event.when_summary,
      link: "when",
    },
    {
      id: 3,
      title: "Where",
      description: event.destination_name,
      link: "who",
    },
    {
      id: 4,
      title: "Stay",
      description: event.accommodations[0].name,
      link: "who",
    },
    {
      id: 5,
      title: "Group size",
      description: `Up to ${event.max_group_size} surfers`,
      link: "where",
    },
  ];

  return (
    <>
      <Meta
        title={`Group trips | ${event.name}`}
        description={event.description}
        path={`/events/${event.slug}`}
        imageOgUrl={event.hero_land?.url}
        imageTwitterUrl={event.hero_land?.url}
        imageType="image/jpeg"
      />
      <Background theme={themeColor}>
        <BannerPhone />
        <Present>
          <Navigation
            departures={event.event_variants.filter((variant) =>
              isFuture(parseISO(variant.start_date))
            )}
          />
          <Hero
            imagePort={event.hero_card}
            imageLand={event.hero_land}
            name={event.name}
            subtitle={event.subtitle}
          />
        </Present>
        <Section>
          <Introduction description={event.description} overview={overview} />
        </Section>
        <Background theme="white">
          <Highlights
            title="Trip Highlights"
            description={event.trip_highlights_subtitle}
            highlights={event.event_highlights}
          />
        </Background>
        <Testimonial
          text={event.testimonial}
          byline={event.testimonial_byline}
          theme="gold"
        />
        <Container>
          <Why
            videoLandFirstFrameSrc={formatURL(
              event.adventure.surf_montage_first_frame?.url
            )}
            videoLandMp4={event.adventure.surf_montage_land_mp4?.url}
            videoLandWebm={event.adventure.surf_montage_land_webm?.url}
            images={event.adventure.surf_images}
            title={`Why surf ${event.destination_short_name}?`}
            description={event.destination_description}
          />
          <Guide
            youtubeURL={event.guide.video_url}
            guideImage={event.guide.main_image}
            videoLandFirstFrameSrc={formatURL(
              event.adventure.guide_montage_first_frame?.url
            )}
            videoLandMp4={event.adventure.guide_montage_land_mp4?.url}
            videoLandWebm={event.adventure.guide_montage_land_webm?.url}
            buttonText={`Meet ${event.guide.first_name}`}
            title={event.guide.title}
            descriptionText={event.guide.story}
          />
        </Container>
        <Accommodation
          title={event.accommodations_title}
          description={event.accommodations_subtitle}
          images={event.accommodations_images}
        />
        <Questions
          faqs={event.faqs}
          image={event.faq_image}
          imageAlt={event.name}
        />
        <Footer color="yellow" />
      </Background>
    </>
  );
}

export async function getStaticPaths() {
  const response = await getEvents();
  if (!response || response.errorMsg) {
    return { paths: [], fallback: "blocking" };
  }

  const paths = response
    .filter((event) => event.event_type === "group_trip" && event.is_visible === true)
    .map((event) => ({ params: { id: event.slug } }));

  return { paths, fallback: "blocking" };
}

export async function getStaticProps({ params }) {
  const response = await getEvent(params.id);
  // replace any undefined values with null to prevent build errors
  const responseString = JSON.stringify(response, (key, value) =>
    value === undefined ? null : value
  );

  if (!response || response.errorMsg) {
    return { notFound: true };
  }
  return {
    props: { event: JSON.parse(responseString) },
    revalidate: 1800,
  };
}

Event.propTypes = {
  event: PropTypes.shape({
    accommodations: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.number,
        is_bookable: PropTypes.bool,
        max_travelers: PropTypes.number,
        min_travelers: PropTypes.number,
        name: PropTypes.string,
      })
    ),
    skill_level: PropTypes.string,
    when_summary: PropTypes.string,
    max_group_size: PropTypes.number,
    destination_name: PropTypes.string,
    destination_short_name: PropTypes.string,
    id: PropTypes.string,
    guide: PropTypes.shape({
      first_name: PropTypes.string,
      title: PropTypes.string,
      story: PropTypes.string,
      video_url: PropTypes.string,
      main_image: PropTypes.shape({
        byte_size: PropTypes.number,
        content_type: PropTypes.string,
        height: PropTypes.number,
        width: PropTypes.number,
        url: PropTypes.string,
      }),
    }),
    main_image: PropTypes.shape({
      url: PropTypes.string,
    }),
    hero_land: PropTypes.shape({
      url: PropTypes.string,
    }),
    hero_card: PropTypes.shape({
      url: PropTypes.string,
    }),
    adventure: PropTypes.shape({
      surf_montage_first_frame: PropTypes.shape({
        url: PropTypes.string,
      }),
      surf_montage_land_mp4: PropTypes.shape({
        url: PropTypes.string,
      }),
      surf_montage_land_webm: PropTypes.shape({
        url: PropTypes.string,
      }),
      guide_montage_first_frame: PropTypes.shape({
        url: PropTypes.string,
      }),
      guide_montage_land_mp4: PropTypes.shape({
        url: PropTypes.string,
      }),
      guide_montage_land_webm: PropTypes.shape({
        url: PropTypes.string,
      }),
      surf_images: PropTypes.arrayOf(
        PropTypes.shape({
          url: PropTypes.string,
        })
      ),
    }),
    event_variants: PropTypes.arrayOf(
      PropTypes.shape({
        start_date: PropTypes.string,
      })
    ),
    accommodations_images: PropTypes.arrayOf(
      PropTypes.shape({
        url: PropTypes.string,
      })
    ),
    accommodation_intro: PropTypes.shape({
      url: PropTypes.string,
    }),
    name: PropTypes.string,
    description: PropTypes.string,
    subtitle: PropTypes.string,
    accommodations_subtitle: PropTypes.string,
    testimonial: PropTypes.string,
    testimonial_byline: PropTypes.string,
    slug: PropTypes.string,
    faqs: PropTypes.objectOf(PropTypes.string),
    theme_color: PropTypes.string,
    trip_highlights_subtitle: PropTypes.string,
    event_highlights: PropTypes.arrayOf(
      PropTypes.shape({
        image: PropTypes.shape({
          url: PropTypes.string,
          content_type: PropTypes.string,
          byte_size: PropTypes.number,
        }),
        title: PropTypes.string,
        description: PropTypes.string,
      })
    ),
    destination_description: PropTypes.string,
    accommodations_title: PropTypes.string,
    faq_image: PropTypes.shape({
      url: PropTypes.string,
    }),
  }),
};

Event.Layout = LayoutResidency;
